"use client";

import { useState } from "react";
import { cx } from "../cx.ts";

/**
 * A grapheme, not a UTF-16 code unit. Slicing `name.slice(0, 2)` cut a Khmer
 * consonant from the subscript it carries — `coeng` joins the next consonant
 * onto the one before it as one cluster, and a code-unit slice has no idea
 * that boundary exists. `Intl.Segmenter` does, for every script at once.
 */
const segmenter = new Intl.Segmenter(undefined, { granularity: "grapheme" });

function graphemes(text: string): string[] {
  return Array.from(segmenter.segment(text), (entry) => entry.segment);
}

/**
 * Two letters: the first grapheme of each word for a two-word name, or the
 * first two graphemes of a one-word name. `toUpperCase` is applied last and
 * is a no-op on a script with no case pairing, which is the correct outcome
 * rather than a gap to fill.
 */
export function initials(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return "";

  const words = trimmed.split(/\s+/);
  const letters =
    words.length > 1
      ? `${graphemes(words[0]!)[0] ?? ""}${graphemes(words[1]!)[0] ?? ""}`
      : graphemes(words[0]!).slice(0, 2).join("");

  return letters.toUpperCase();
}

export type AvatarProps = {
  name: string;
  /** Image URL; absent when the person has no photo, falling back to initials. */
  src?: string;
  /** Edge length in CSS pixels. */
  size?: number;
  className?: string;
};

export function Avatar({ name, src, size = 32, className }: AvatarProps) {
  // A source that 404s or times out should not leave a browser's broken-image
  // icon sitting in the sidebar — initials are always renderable, a URL is not.
  const [broken, setBroken] = useState(false);
  const showImage = Boolean(src) && !broken;

  return (
    <span
      className={cx(
        "inline-flex shrink-0 items-center justify-center overflow-hidden rounded-pill bg-primary-soft font-semibold text-primary-ink",
        className,
      )}
      // `size` is the circle's edge in CSS pixels, so the initials are sized
      // in pixels too, tied to the circle rather than to the reader's root
      // font-size: a `rem`-based size (what `.text-xs` gave before it) would
      // grow past a circle that does not grow with it. That also drops
      // `.text-xs`'s line-height, which costs nothing here — `items-center`
      // centers the text inside the fixed-height box regardless of it.
      style={{ width: size, height: size, fontSize: Math.round(size * 0.375) }}
    >
      {showImage ? (
        // A plain <img>, never next/image: this package has no framework and
        // the till renders it too (`ui-framework-agnostic.test.ts`).
        <img src={src} alt="" className="size-full object-cover" onError={() => setBroken(true)} />
      ) : (
        initials(name)
      )}
    </span>
  );
}
